/**
 * EverOut Slider — Interaction Controller
 *
 * Handles arrow navigation, progress dots, keyboard nav,
 * and mobile action trigger toggle for .eo-slider elements.
 *
 * Does NOT handle Like/Remind/Add-to-List — those are already
 * handled by member-actions.js and reminders.js via class-based
 * event delegation.
 */

class EOSlider {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.eo-slider__track');
    if (!this.track) return;

    this.prevBtn = el.querySelector('.eo-slider__arrow--prev');
    this.nextBtn = el.querySelector('.eo-slider__arrow--next');
    this.progressContainer = el.querySelector('.eo-slider__progress');
    this.cards = Array.from(this.track.children);

    if (!this.cards.length) return;

    this.isHovering = false;
    this.scrollTimeout = null;
    this.dots = [];
    this.pageCount = 0;

    this.init();
  }

  init() {
    this.buildProgress();
    this.bindEvents();
    requestAnimationFrame(() => this.updateState());
  }

  buildProgress() {
    if (!this.progressContainer || this.cards.length <= 1) return;

    const trackWidth = this.track.clientWidth;
    const cardWidth = this.cards[0].offsetWidth;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const visibleCards = Math.floor(trackWidth / (cardWidth + gap)) || 1;
    this.pageCount = Math.ceil(this.cards.length / visibleCards);

    this.progressContainer.innerHTML = '';
    this.dots = [];

    for (let i = 0; i < this.pageCount; i++) {
      const dot = document.createElement('div');
      dot.classList.add('eo-slider__dot');
      if (i === 0) dot.classList.add('eo-slider__dot--active');
      this.progressContainer.appendChild(dot);
      this.dots.push(dot);
    }
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.scroll(-1));
    this.nextBtn?.addEventListener('click', () => this.scroll(1));

    this.track.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => this.updateState(), 60);
    }, { passive: true });

    this.el.addEventListener('mouseenter', () => {
      this.isHovering = true;
      this.updateState();
    });
    this.el.addEventListener('mouseleave', () => {
      this.isHovering = false;
      this.updateState();
    });

    // Keyboard navigation
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.scroll(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.scroll(1);
      }
    });

    // Debounced resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.buildProgress();
        this.updateState();
      }, 200);
    });
  }

  scroll(direction) {
    const trackWidth = this.track.clientWidth;
    const scrollAmount = (trackWidth - 60) * direction;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: scrollAmount, behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  updateState() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft >= maxScroll - 2;
    const overflows = scrollWidth > clientWidth + 4;

    // Arrow visibility
    if (this.prevBtn) {
      this.prevBtn.classList.toggle(
        'eo-slider__arrow--visible',
        overflows && !atStart && this.isHovering
      );
    }
    if (this.nextBtn) {
      this.nextBtn.classList.toggle(
        'eo-slider__arrow--visible',
        overflows && !atEnd && this.isHovering
      );
    }

    // Progress dots
    if (this.dots.length > 0 && maxScroll > 0) {
      const progress = scrollLeft / maxScroll;
      const activeDot = Math.round(progress * (this.dots.length - 1));
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('eo-slider__dot--active', i === activeDot);
      });
    }
  }
}

// Initialize all sliders on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.eo-slider').forEach(el => new EOSlider(el));
});
